// packages/web/src/pages/FAQPage.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Header } from '../components/Layout/Header';
import { Footer } from '../components/Layout/Footer';
import { LoginDialog } from '../components/Auth/LoginDialog';
import { useAuth } from '../hooks/useAuth';

const PageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  backgroundColor: '#ffffff',
});

const HeroSection = styled(Box)(({ theme }) => ({
  backgroundColor: '#fafafa',
  padding: theme.spacing(8, 0, 6, 0),
  textAlign: 'center',
  borderBottom: '1px solid #e5e5e5',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0, 4, 0),
  },
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '3rem',
  fontWeight: 700,
  lineHeight: 1.2,
  marginBottom: theme.spacing(2),
  color: '#1a1a1a',
  letterSpacing: '-0.02em',
  [theme.breakpoints.down('md')]: {
    fontSize: '2.5rem',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '2rem',
  },
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
  maxWidth: '700px',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 0),
  },
}));

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: '1px solid #e5e5e5',
  borderRadius: '12px !important',
  boxShadow: 'none',
  '&:before': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: `${theme.spacing(2)} 0`,
    borderColor: '#c4d600',
  },
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1.5, 0),
  },
}));

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3, 3),
  borderTop: '1px solid #e5e5e5',
}));

const AnswerText = styled(Typography)(({ theme }) => ({
  fontSize: '1rem',
  color: '#666',
  lineHeight: 1.7,
  fontWeight: 400,
}));

const CategoryTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.75rem',
  fontWeight: 700,
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(5),
  color: '#1a1a1a',
  letterSpacing: '-0.01em',
  '&:first-of-type': {
    marginTop: 0,
  },
}));

const FAQPage: React.FC = () => {
  const { user, signOut } = useAuth();
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqData = [
    {
      category: 'Getting Started',
      questions: [
        {
          id: 'panel1',
          question: 'How do I create an account?',
          answer: 'Click on "Sign In" in the top right corner and use your Google account to sign up. It\'s quick and secure - no passwords to remember!',
        },
        {
          id: 'panel2',
          question: 'Is Sched Tennis free to use?',
          answer: 'Yes! Sched Tennis is completely free to use. You can publish your schedule, challenge other players, and coordinate matches without any cost.',
        },
        {
          id: 'panel3',
          question: 'Do I need to download an app?',
          answer: 'No download required! Sched Tennis works directly in your web browser on both desktop and mobile devices. However, we do offer mobile-optimized views for on-the-go scheduling.',
        },
      ],
    },
    {
      category: 'Publishing Your Schedule',
      questions: [
        {
          id: 'panel4',
          question: 'How do I publish my availability?',
          answer: 'Go to your personal schedule page and click on time slots to mark when you\'re available to play. You can set time blocks from 30 minutes to 5 hours. Don\'t forget to specify which court you\'ll be playing at!',
        },
        {
          id: 'panel5',
          question: 'Can I make my schedule private?',
          answer: 'Yes! You can choose to publish your calendar publicly or keep it accessible only via a private link. This is perfect for playing with specific groups or friends.',
        },
        {
          id: 'panel6',
          question: 'Can I update my schedule after publishing?',
          answer: 'Absolutely! You can edit your schedule anytime. Just remember that if you have pending challenges for specific time slots, canceling those slots will automatically withdraw those challenges.',
        },
      ],
    },
    {
      category: 'Finding and Challenging Players',
      questions: [
        {
          id: 'panel7',
          question: 'How does the calendar heatmap work?',
          answer: 'The main calendar shows a 7-day view with color-coded time slots. The colors represent how many players are available at each time - darker colors mean more players are available, making it easier to find busy times.',
        },
        {
          id: 'panel8',
          question: 'What filters can I use to find players?',
          answer: 'You can filter by location (city, country, or coordinates), court name, player skill level, recent site activity, distance from your location, and match duration.',
        },
        {
          id: 'panel9',
          question: 'Can I challenge multiple players for the same time slot?',
          answer: 'Yes! You can send challenges to as many players as you want. Once any player accepts, all other pending challenges for that time slot are automatically withdrawn.',
        },
        {
          id: 'panel10',
          question: 'What happens after I challenge someone?',
          answer: 'The player receives a notification of your challenge. They can either accept or reject it. You\'ll be notified of their response, and if accepted, the match is confirmed!',
        },
      ],
    },
    {
      category: 'Managing Challenges',
      questions: [
        {
          id: 'panel11',
          question: 'Where can I see challenges I\'ve received?',
          answer: 'Go to your personal profile page and navigate to the "Challenges" section. There you\'ll see all pending challenges with options to accept or reject each one.',
        },
        {
          id: 'panel12',
          question: 'Can I cancel a challenge after sending it?',
          answer: 'Yes, you can withdraw a challenge before it\'s been accepted. Just go to your challenges page and select the challenge you want to cancel.',
        },
        {
          id: 'panel13',
          question: 'What if I need to cancel an accepted match?',
          answer: 'If you need to cancel a confirmed match, please do so as early as possible out of courtesy to the other player. You can cancel from your schedule page.',
        },
      ],
    },
    {
      category: 'Courts and Locations',
      questions: [
        {
          id: 'panel14',
          question: 'Do I need to specify a court when publishing my schedule?',
          answer: 'While not required, we highly recommend specifying which court you\'ll be at. This helps other players know exactly where to meet you and makes coordination much smoother.',
        },
        {
          id: 'panel15',
          question: 'Can I search for courts in my area?',
          answer: 'Yes! Use the "Find Courts" feature to browse tennis courts in your area. You can view court details, locations, and see which players are scheduling matches there.',
        },
        {
          id: 'panel16',
          question: 'What if my court isn\'t listed?',
          answer: 'You can still specify the court name when creating your time slots. If you\'d like to add a new court to our database, please contact us with the court details.',
        },
      ],
    },
  ];

  return (
    <PageContainer>
      <Header user={user} onSignOut={signOut} onLoginClick={handleLoginClick} />

      <HeroSection>
        <Container maxWidth="md">
          <PageTitle>Frequently Asked Questions</PageTitle>
          <PageSubtitle>
            Everything you need to know about scheduling tennis matches on Sched Tennis
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container maxWidth="md">
          {faqData.map((category, categoryIndex) => (
            <Box key={categoryIndex}>
              <CategoryTitle>{category.category}</CategoryTitle>
              {category.questions.map((faq) => (
                <StyledAccordion
                  key={faq.id}
                  expanded={expanded === faq.id}
                  onChange={handleChange(faq.id)}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#84b817' }} />}
                  >
                    <QuestionText>{faq.question}</QuestionText>
                  </StyledAccordionSummary>
                  <StyledAccordionDetails>
                    <AnswerText>{faq.answer}</AnswerText>
                  </StyledAccordionDetails>
                </StyledAccordion>
              ))}
            </Box>
          ))}
        </Container>
      </ContentSection>

      <Footer />

      <LoginDialog
        open={loginDialogOpen}
        onClose={() => setLoginDialogOpen(false)}
      />
    </PageContainer>
  );
};

export default FAQPage;
